import { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { useChartTheme } from '../../../../utils/chartTheme';
import BandDonut from '../../../components/Feedback/BandDonut.jsx';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Filler, Tooltip);

/* =============================================================
   The classroom app's charts: the dashboard Overview and Reports.

   Every series arrives worked out by the API
   (classroommanagement.service.js: buildLabsByHour, buildHoursTrend,
   buildRanking, buildOutcomes); these components only draw it. Each
   point or bar may carry a `tip` -- the tooltip's words, written by the
   page -- so a tooltip needs no callback built per render.

   Colour, per instruction_ui.json "charts":
   - the line and the bars are ONE measure each, so series slot 1 (the
     palette's accent), no legend, and the panel title names it;
   - the doughnuts are STATE (free / occupied / out of order, held /
     cancelled), so literal status colours from the maps below, each
     slice with its count in the key beside the ring. Never both kinds
     in one chart.
   The frame is the house chart panel, .br-chart in lms-pages.css.
   ============================================================= */

/* Status colours, the pill inks (TrainerDashboard.css .pg-pill--good /
   --live / --warn / --bad) as solid marks. */
export const LAB_STATUS_TONE = { free: '#0E7C6B', occupied: '#3A57C9', maintenance: '#B3202E' };
export const TRAINER_STATUS_TONE = { teaching: '#3A57C9', free: '#0E7C6B', absent: '#B3202E' };
export const SESSION_OUTCOME_TONE = { planned: '#0E7C6B', moved: '#B9700C', cancelled: '#B3202E', leave: '#667085' };

const formatValue = (value, unit) => {
  if (value === null || value === undefined || value === '') return '—';
  return `${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 1 })}${unit || ''}`;
};

const trimLabel = (label, max) => {
  const s = String(label ?? '');
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const ChartPanel = ({ title, sub, note, className, children }) => (
  <section className={`br-chart${className ? ` ${className}` : ''}`} aria-label={title}>
    <header className="br-chart-head">
      <div>
        <h5 className="br-chart-title">{title}</h5>
        {sub && <p className="br-chart-sub">{sub}</p>}
        {note && <p className="br-chart-sub">{note}</p>}
      </div>
    </header>
    {children}
  </section>
);

/** One measure over time, as a line with a 10% wash. points: [{ label, value, tip }] */
export const TrendChart = ({ title, sub, points = [], unit, empty, className }) => {
  const theme = useChartTheme();
  const hasData = points.some((p) => Number(p.value) > 0);

  const data = useMemo(() => {
    const colour = theme.series[0];
    return {
      labels: points.map((p) => p.label),
      datasets: [
        {
          label: title,
          data: points.map((p) => p.value),
          borderColor: colour,
          backgroundColor: theme.wash(colour, 0.1),
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: colour,
          pointBorderColor: theme.surface,
          pointBorderWidth: 2,
          tension: 0.3,
          fill: true,
        },
      ],
    };
  }, [points, title, theme]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...theme.tooltip,
          callbacks: {
            label: (ctx) => points[ctx.dataIndex]?.tip || ` ${formatValue(ctx.parsed.y, unit)}`,
          },
        },
      },
      scales: {
        x: theme.categoryAxis({ ticks: { autoSkip: true, maxRotation: 0 } }),
        y: theme.valueAxis({ beginAtZero: true, ticks: { callback: (v) => formatValue(v, unit) } }),
      },
    }),
    [points, unit, theme],
  );

  return (
    <ChartPanel title={title} sub={sub} className={className}>
      {hasData ? (
        <div
          className="br-chart-plot br-chart-plot--line"
          role="img"
          aria-label={`${title}: ${points.map((p) => `${p.label} ${formatValue(p.value, unit)}`).join(', ')}`}
        >
          <Line data={data} options={options} />
        </div>
      ) : (
        <p className="br-chart-state">{empty}</p>
      )}
    </ChartPanel>
  );
};

/** One measure across names or hours, as bars. bars: [{ label, value, tip }] */
export const BarsChart = ({ title, sub, note, bars = [], unit, max, horizontal = false, empty, className }) => {
  const theme = useChartTheme();
  const hasData = bars.some((b) => Number(b.value) > 0);

  const data = useMemo(
    () => ({
      labels: bars.map((b) => b.label),
      datasets: [
        {
          label: title,
          data: bars.map((b) => b.value),
          backgroundColor: theme.series[0],
          hoverBackgroundColor: theme.series[0],
          // Rounded at the value, square at the baseline it grows from.
          borderRadius: 4,
          borderSkipped: 'start',
          maxBarThickness: 24,
        },
      ],
    }),
    [bars, title, theme],
  );

  const options = useMemo(() => {
    const valueAxis = theme.valueAxis({
      beginAtZero: true,
      ...(max ? { max } : {}),
      ticks: { callback: (v) => formatValue(v, unit) },
    });
    const categoryAxis = theme.categoryAxis({
      ticks: {
        autoSkip: !horizontal,
        maxRotation: 0,
        callback(value) {
          return trimLabel(this.getLabelForValue(value), horizontal ? 24 : 8);
        },
      },
    });
    return {
      indexAxis: horizontal ? 'y' : 'x',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          ...theme.tooltip,
          callbacks: {
            label: (ctx) => bars[ctx.dataIndex]?.tip || ` ${formatValue(horizontal ? ctx.parsed.x : ctx.parsed.y, unit)}`,
          },
        },
      },
      scales: horizontal ? { x: valueAxis, y: categoryAxis } : { x: categoryAxis, y: valueAxis },
    };
  }, [bars, unit, max, horizontal, theme]);

  // Height follows the bar count on a horizontal chart -- the one inline
  // style the house chart panel allows (lms-pages.css .br-chart-plot).
  const height = horizontal ? Math.max(200, bars.length * 30 + 48) : 240;

  return (
    <ChartPanel title={title} sub={sub} note={note} className={className}>
      {hasData ? (
        <div
          className="br-chart-plot"
          role="img"
          aria-label={`${title}: ${bars.map((b) => `${b.label} ${formatValue(b.value, unit)}`).join(', ')}`}
          style={{ height: `${height}px` }}
        >
          <Bar data={data} options={options} />
        </div>
      ) : (
        <p className="br-chart-state">{empty}</p>
      )}
    </ChartPanel>
  );
};

/** A count split by state: the house doughnut (Feedback/BandDonut) in a chart panel. */
export const StatusDonut = ({ title, sub, segments, centreValue, centreLabel, empty, className }) => (
  <ChartPanel title={title} sub={sub} className={className}>
    <BandDonut segments={segments} centreValue={centreValue} centreLabel={centreLabel} empty={empty} />
  </ChartPanel>
);
